import { useQueries } from '@tanstack/react-query'
import { useOrgUnitProjectIds } from '././useOrgUnitProjectIds'
import { useAccessToken } from '../../../useAccessToken'
import { makeResultFetch } from '../../../genericFetch'
import { apiRequestParams } from '../../../fetchOptions'
import { useHandleRetry } from '../../../useHandleRetry'
import { GridReportInputs } from '../../../../interfaces/reports/ResCalcProjReport'
import { ForecastResCalcData } from '../../../../interfaces/reports/ForecastResCalcReport'
import PQueue from 'p-queue'
import { useCallback } from 'react'

const useFetch = (fromDate: string, toDate: string) => {
  const getAccessToken = useAccessToken()

  const queue = new PQueue({ concurrency: 10 })

  const queryFunction = async (
    projectId: string,
    signal?: AbortSignal | null
  ) =>
    await queue.add(() =>
      getAccessToken().then((accessToken) => {
        if (!accessToken) throw new Error('Access token is undefined')

        const fetchedData = makeResultFetch<ForecastResCalcData>(
          `/Reports/${projectId}/MonthlyForecastedRevenueRecognitionSummary?from=${fromDate}&to=${toDate}`,
          apiRequestParams(accessToken, 'GET', null, signal)
        ).then((response) => {
          const { data, message, success } = response

          if (!success) throw Error(message)

          return data
        })

        return fetchedData
      })
    )

  return queryFunction
}

export const useForeCastResCalcFlow = ({
  orgUnitIds,
  fromDate,
  toDate,
}: GridReportInputs) => {
  const queryFunction = useFetch(fromDate, toDate)

  const handleRetry = useHandleRetry()
  const projectIds = useOrgUnitProjectIds(orgUnitIds, []).data

  const queryEnabled =
    !!queryFunction &&
    projectIds &&
    projectIds?.length > 0 &&
    fromDate?.length > 0 &&
    toDate?.length > 0

  const mapQueries = useCallback(() => {
    if (projectIds) {
      return projectIds.map((projectId) => {
        const query = {
          queryKey: ['forecastResCalcReport', projectId, fromDate, toDate],
          queryFn: ({ signal }: { signal?: AbortSignal }) =>
            queryFunction(projectId, signal),
          enabled: queryEnabled,
          retry: handleRetry,
        }

        return query
      })
    }

    return []
  }, [fromDate, handleRetry, projectIds, queryEnabled, queryFunction, toDate])

  const report = useQueries({
    queries: mapQueries(),
  })

  return report
}
